import LocalizedString from './localized-string';

export type RiskProfileRule = RiskProfileAssetAllocationRule | RiskProfileFundTypeRule | RiskProfileRiskLimitRule | RiskProfileAllowedRiskRule;

export enum RiskProfileRuleType {
  AssetAllocation = 'ASSET_ALLOCATION',
  FundType = 'FUND_TYPE',
  RiskLimit = 'RISK_LIMIT',
  AllowedRisk = 'ALLOWED_RISK',
}

export type RiskProfileRuleTarget = {
  key: string;
  label: string;
  value: string;
};

export enum RiskAssetType {
  MoneyMarket = 'MONEY_MARKET',
  Income = 'INCOME',
  IncomeGrowth = 'INCOME_GROWTH',
  Growth = 'GROWTH',
  Other = 'OTHER',
}

export enum InvestmentRiskLevel {
  VeryLow = 'VERY_LOW',
  Low = 'LOW',
  LowMedium = 'LOW_TO_MEDIUM',
  Medium = 'MEDIUM',
  MediumHigh = 'MEDIUM_TO_HIGH',
  High = 'HIGH',
  VeryHigh = 'VERY_HIGH',
}

export enum RiskLevelOperator {
  GT = 'GT',
  GTE = 'GTE',
  EQ = 'EQ',
  LTE = 'LTE',
  LT = 'LT',
  CD = 'CD', // condensed rules mode
}

export enum InvestmentAssetType {
  Cash = 'CASH',
  FixedIncome = 'FIXED_INCOME',
  Equity = 'EQUITY',
  RealAssets = 'REAL_ASSETS',
  Other = 'OTHER',
  AlternativeInvestment = 'ALTERNATIVE_INVESTMENT',
}

export type BaseRiskProfileRule = {
  ruleType: RiskProfileRuleType;
};

export type RiskProfileAssetAllocationRule = BaseRiskProfileRule & {
  ruleType: RiskProfileRuleType.AssetAllocation;
  value: Array<{
    assetType: InvestmentAssetType;
    min: number;
    max: number;
  }>;
};

export type RiskProfileFundTypeRule = BaseRiskProfileRule & {
  ruleType: RiskProfileRuleType.FundType;
  labels?: {
    description?: LocalizedString;
  };
  value: {
    fundType: RiskAssetType;
    min: number;
  };
};

export type RiskProfileRiskLimitRule = BaseRiskProfileRule & {
  ruleType: RiskProfileRuleType.RiskLimit;
  labels?: {
    description?: LocalizedString;
  };
  value: {
    riskLevel: InvestmentRiskLevel;
    min: number;
  };
};

export type RiskProfileAllowedRiskRule = BaseRiskProfileRule & {
  ruleType: RiskProfileRuleType.AllowedRisk;
  labels?: {
    description?: LocalizedString;
  };
  value: {
    operator: RiskLevelOperator;
    riskLevel: InvestmentRiskLevel;
    max: number;
  };
};

export type RiskProfileRuleData = {
  key: string;
  label: string;
  value?: number;
  adjustment?: number;
  subAllocations?: RiskProfileRuleData[]; // For Asset Allocation rules, this contains the sub-allocations
  hidden?: boolean; // Whether this data should be hidden from the profile description (ie: legends)
  target: {
    min?: number;
    max?: number;
  };
};

export type RiskProfileConfigRule = RiskProfileAllowedRiskRule | RiskProfileAssetAllocationRule | RiskProfileFundTypeRule | RiskProfileRiskLimitRule;
